import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import axios from "axios";
import { API_URL } from "../../config";
import {
    ContentHeader,
    ContentTitleBlock,
    Chip,
    Subtitle,
    TabsNav,
    Tab,
    ContentActionsRow,
    BtnIcon
} from "../../components/SharedPageStyles";
import { SECTIONS } from "../../constants/sections";
import GlobalIndicatorsTab from "./GlobalIndicatorsTab";
import CentralBanksTab from "./CentralBanksTab";
import CountryHealthTab from "./CountryHealthTab";

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0px;
    flex: 1;
    min-height: 0;
`;

const LoadingBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 320px;
    gap: 12px;
    color: #80cbc4;
    font-size: 13px;
    font-weight: 600;
`;

const Spinner = styled.div`
    width: 28px;
    height: 28px;
    border: 3px solid rgba(128, 203, 196, 0.2);
    border-top-color: #80cbc4;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        to { transform: rotate(360deg); }
    }
`;

const ErrorBox = styled.div`
    background: rgba(239, 83, 80, 0.1);
    border: 1px solid rgba(239, 83, 80, 0.3);
    border-radius: 6px;
    padding: 14px 18px;
    color: #ef5350;
    font-size: 13px;
    margin-top: 10px;
`;

const EconomyPage = () => {
    const tabs = SECTIONS["economy"]?.tabs || [
        { key: "country-health", label: "Economic Health" },
        { key: "indicators", label: "Economic Indicators" },
        { key: "central-banks", label: "Central Banks" }
    ];

    const [activeTab, setActiveTab] = useState(tabs[0].key);
    const [selectedCountryId, setSelectedCountryId] = useState("US");
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const res = await axios.get(`${API_URL}/economy/overview?t=${Date.now()}`);
            setData(res.data);
        } catch (err) {
            console.error("Failed to fetch economy overview:", err);
            setError(err.message || "Failed to load economy data");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        const interval = setInterval(() => {
            fetchData();
        }, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const handleSelectCountry = (countryId) => {
        setSelectedCountryId(countryId);
        setActiveTab("country-health");
    };

    const renderContent = () => {
        if (isLoading && !data) {
            return (
                <LoadingBox>
                    <Spinner />
                    <span>Chargement des données...</span>
                </LoadingBox>
            );
        }

        if (error && !data) {
            return (
                <ErrorBox>
                    Erreur de chargement: {error}.
                </ErrorBox>
            );
        }

        switch (activeTab) {
            case "central-banks":
                return <CentralBanksTab data={data} />;
            case "country-health":
                return (
                    <CountryHealthTab
                        data={data}
                        selectedCountryId={selectedCountryId}
                        onSelectCountry={setSelectedCountryId}
                    />
                );

            case "indicators":
            default:
                return (
                    <GlobalIndicatorsTab
                        data={data}
                        onSelectCountry={handleSelectCountry}
                    />
                );
        }
    };

    return (
        <PageContainer>
            <ContentHeader>
                <ContentTitleBlock>
                    <Chip>Economy</Chip>
                    <Subtitle>Macroeconomic indicators & central bank rates</Subtitle>
                </ContentTitleBlock>
            </ContentHeader>

            <ContentActionsRow style={{ marginTop: '18px' }}>
                <BtnIcon
                    aria-label="Refresh data"
                    onClick={fetchData}
                    title="Refresh data"
                    disabled={isLoading}
                    $spinning={isLoading}
                >
                    ⟳
                </BtnIcon>
            </ContentActionsRow>

            {/* Spacer to align tabs vertically with other pages */}
            <div style={{ minHeight: '24px', flexShrink: 0 }}></div>

            <TabsNav>
                {tabs.map(tab => (
                    <Tab
                        key={tab.key}
                        active={activeTab === tab.key}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </Tab>
                ))}
            </TabsNav>

            <div style={{ marginTop: '20px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {renderContent()}
            </div>
        </PageContainer>
    );
};

export default EconomyPage;
